import { useState } from 'react';
import { LayoutDashboard, Radio, Truck } from 'lucide-react';
import { computeTowerSummary } from './dispatch/mockDispatchData';
import UrgentAlertsBanner from './dispatch/UrgentAlertsBanner';
import LiveTripMonitorTable from './dispatch/LiveTripMonitorTable';
import QuickReroutePanel from './dispatch/QuickReroutePanel';

const KPI_CHIPS = (summary) => [
  {
    label: 'Active Trips',
    value: summary.activeTrips,
    className: 'border-logi-grid bg-white',
    valueClass: 'text-logi-text',
  },
  {
    label: 'On Schedule',
    value: summary.onSchedule,
    className: 'border-emerald-200 bg-emerald-50',
    valueClass: 'text-emerald-700',
  },
  {
    label: 'Delayed',
    value: summary.delayed,
    className: 'border-red-200 bg-red-50',
    valueClass: 'text-red-700',
  },
  {
    label: 'Avg Progress',
    value: `${summary.avgProgress}%`,
    className: 'border-logi-accent/25 bg-logi-accent/10',
    valueClass: 'text-logi-accent',
  },
];

export default function DispatchControlTower() {
  const [selectedTripId, setSelectedTripId] = useState('TRP-24018');
  const [rerouteLog, setRerouteLog] = useState(null);
  const summary = computeTowerSummary();

  const handleAlertAction = (alert) => {
    if (alert.tripId) setSelectedTripId(alert.tripId);
  };

  const handleRerouteApply = (payload) => {
    setRerouteLog(payload);
  };

  return (
    <section
      aria-label="Dispatch control tower"
      className="flex min-h-[calc(100dvh-10rem)] w-full flex-col gap-4 overflow-hidden"
    >
      <header className="shrink-0 border-b border-logi-grid pb-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-logi-accent/10 ring-1 ring-logi-accent/30">
                <LayoutDashboard className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-logi-accent/80">
                OGTS LogiCore™ · Mission Control
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700 ring-1 ring-emerald-500/30">
                <Radio className="h-2.5 w-2.5 animate-pulse" />
                Live
              </span>
            </div>
            <h2 className="text-lg font-bold tracking-tight text-logi-text sm:text-xl">
              Dispatch Control Tower
            </h2>
            <p className="mt-0.5 text-xs text-logi-muted">
              Active trip monitoring · Instant re-routing · Large-screen ops
            </p>
          </div>

          <div className="grid shrink-0 grid-cols-2 gap-2 sm:grid-cols-4 xl:max-w-xl">
            {KPI_CHIPS(summary).map((chip) => (
              <div
                key={chip.label}
                className={`rounded-lg border px-2.5 py-2 text-center ${chip.className}`}
              >
                <p className="text-[9px] font-semibold uppercase tracking-wider text-logi-text0">
                  {chip.label}
                </p>
                <p className={`font-mono text-base font-bold tabular-nums ${chip.valueClass}`}>
                  {chip.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </header>

      <div className="shrink-0">
        <UrgentAlertsBanner selectedTripId={selectedTripId} onAlertAction={handleAlertAction} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 xl:flex-row xl:items-stretch">
        <LiveTripMonitorTable selectedTripId={selectedTripId} onSelectTrip={setSelectedTripId} />
        <QuickReroutePanel
          selectedTripId={selectedTripId}
          onSelectTrip={setSelectedTripId}
          onApply={handleRerouteApply}
        />
      </div>

      {rerouteLog && (
        <div className="flex shrink-0 items-center gap-2 rounded-lg border border-cyan-500/20 bg-logi-accent/10 px-3 py-2 text-[11px] text-logi-accent">
          <Truck className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            Last action: <strong className="text-logi-accent">{rerouteLog.tripId}</strong> →{' '}
            {rerouteLog.route} · {rerouteLog.driver}
          </span>
        </div>
      )}
    </section>
  );
}
